import React, { useMemo, useState } from 'react';

export type DimensionPreset = {
  id: string;
  label: string;
  width: number;
  height: number;
};

export type DimensionCategory = {
  id: string;
  label: string;
  presets: DimensionPreset[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  onApply?: (size: { width: number; height: number }) => void;
};

const DEFAULT_CATEGORIES: DimensionCategory[] = [
  {
    id: 'common',
    label: 'Common',
    presets: [
      { id: 'full-hd', label: 'Full HD (1920×1080)', width: 1920, height: 1080 },
      { id: 'hd', label: 'HD (1280×720)', width: 1280, height: 720 },
      { id: 'square-1080', label: 'Square (1080×1080)', width: 1080, height: 1080 },
      { id: 'portrait-1080x1350', label: 'Portrait (1080×1350)', width: 1080, height: 1350 },
      { id: 'banner-1200x400', label: 'Banner (1200×400)', width: 1200, height: 400 },
    ],
  },
  {
    id: 'social',
    label: 'Social',
    presets: [
      { id: 'ig-post', label: 'Instagram Post (1080×1080)', width: 1080, height: 1080 },
      { id: 'ig-story', label: 'Instagram Story (1080×1920)', width: 1080, height: 1920 },
      { id: 'fb-cover', label: 'Facebook Cover (1640×624)', width: 1640, height: 624 },
      { id: 'yt-thumb', label: 'YouTube Thumb (1280×720)', width: 1280, height: 720 },
    ],
  },
  {
    id: 'ads',
    label: 'Ads',
    presets: [
      { id: 'leaderboard', label: 'Leaderboard (728×90)', width: 728, height: 90 },
      { id: 'medium-rect', label: 'Medium Rectangle (300×250)', width: 300, height: 250 },
      { id: 'skyscraper', label: 'Skyscraper (160×600)', width: 160, height: 600 },
    ],
  },
  // Print-oriented categories (300 DPI pixel equivalents)
  {
    id: 'print-a-series',
    label: 'Print · A‑Series (300 DPI)',
    presets: [
      { id: 'a5-portrait', label: 'A5 Portrait (1748×2480)', width: 1748, height: 2480 },
      { id: 'a5-landscape', label: 'A5 Landscape (2480×1748)', width: 2480, height: 1748 },
      { id: 'a4-portrait', label: 'A4 Portrait (2480×3508)', width: 2480, height: 3508 },
      { id: 'a4-landscape', label: 'A4 Landscape (3508×2480)', width: 3508, height: 2480 },
      { id: 'a3-portrait', label: 'A3 Portrait (3508×4961)', width: 3508, height: 4961 },
      { id: 'a3-landscape', label: 'A3 Landscape (4961×3508)', width: 4961, height: 3508 },
      { id: 'a2-portrait', label: 'A2 Portrait (4961×7016)', width: 4961, height: 7016 },
      { id: 'a2-landscape', label: 'A2 Landscape (7016×4961)', width: 7016, height: 4961 },
    ],
  },
  {
    id: 'print-us',
    label: 'Print · US Sizes (300 DPI)',
    presets: [
      { id: 'letter-portrait', label: 'US Letter Portrait (2550×3300)', width: 2550, height: 3300 },
      { id: 'letter-landscape', label: 'US Letter Landscape (3300×2550)', width: 3300, height: 2550 },
      { id: 'legal-portrait', label: 'US Legal Portrait (2550×4200)', width: 2550, height: 4200 },
      { id: 'legal-landscape', label: 'US Legal Landscape (4200×2550)', width: 4200, height: 2550 },
      { id: 'tabloid-portrait', label: 'Tabloid 11×17 Portrait (3300×5100)', width: 3300, height: 5100 },
      { id: 'tabloid-landscape', label: 'Tabloid 17×11 Landscape (5100×3300)', width: 5100, height: 3300 },
    ],
  },
  {
    id: 'posters',
    label: 'Posters (300 DPI)',
    presets: [
      { id: 'poster-11x17', label: '11×17 (3300×5100)', width: 3300, height: 5100 },
      { id: 'poster-18x24', label: '18×24 (5400×7200)', width: 5400, height: 7200 },
      { id: 'poster-24x36', label: '24×36 (7200×10800)', width: 7200, height: 10800 },
      { id: 'poster-27x40', label: '27×40 (8100×12000)', width: 8100, height: 12000 },
    ],
  },
  {
    id: 'flyers',
    label: 'Flyers / Handouts (300 DPI)',
    presets: [
      { id: 'flyer-half-letter', label: 'Half Letter 5.5×8.5 (1650×2550)', width: 1650, height: 2550 },
      { id: 'flyer-a5', label: 'A5 (1748×2480)', width: 1748, height: 2480 },
      { id: 'flyer-dl', label: 'DL 99×210mm (1169×2480)', width: 1169, height: 2480 },
      { id: 'flyer-square-6in', label: 'Square 6×6 in (1800×1800)', width: 1800, height: 1800 },
    ],
  },
  {
    id: 'business-cards',
    label: 'Business Cards (300 DPI)',
    presets: [
      { id: 'bc-us', label: 'US 3.5×2 in (1050×600)', width: 1050, height: 600 },
      { id: 'bc-eu', label: 'EU 85×55 mm (1004×649)', width: 1004, height: 649 },
      { id: 'bc-square-2.5', label: 'Square 2.5×2.5 in (750×750)', width: 750, height: 750 },
    ],
  },
];

const DimensionPickerModal: React.FC<Props> = ({ open, onClose, onApply }) => {
  const [categories] = useState<DimensionCategory[]>(DEFAULT_CATEGORIES);
  const [activeCat, setActiveCat] = useState<string>(categories[0]?.id || 'common');
  const [selected, setSelected] = useState<DimensionPreset | null>(categories[0]?.presets?.[0] || null);
  const [customW, setCustomW] = useState<string>('');
  const [customH, setCustomH] = useState<string>('');

  const active = useMemo(() => categories.find(c => c.id === activeCat) || categories[0], [categories, activeCat]);

  const width = selected?.width || Number(customW) || 1200;
  const height = selected?.height || Number(customH) || 800;

  const preview = useMemo(() => {
    const maxW = 520; // px within preview area
    const maxH = 360; // px within preview area
    const ar = width / height;
    let w = maxW;
    let h = Math.round(w / ar);
    if (h > maxH) {
      h = maxH;
      w = Math.round(h * ar);
    }
    return { w, h };
  }, [width, height]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center" onMouseDown={onClose}>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(79,70,229,0.12),transparent_35%),radial-gradient(ellipse_at_bottom_left,rgba(236,72,153,0.12),transparent_35%)]" />
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative z-10 w-[min(1100px,96vw)] max-h-[88vh] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden" onMouseDown={(e)=>e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white text-indigo-600 ring-1 ring-indigo-200 shadow-sm">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <path d="M3 9h18M9 21V9"/>
              </svg>
            </span>
            <div className="font-semibold text-gray-900">Dimension Picker</div>
          </div>
          <button className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-black/5" onClick={onClose} aria-label="Close">
            <svg className="h-4 w-4 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] min-h-0" style={{height:'calc(88vh - 52px)'}}>
          {/* Left: categories and presets */}
          <aside className="border-r border-gray-100 bg-gray-50/60 overflow-y-auto h-full min-h-0">
            <div className="p-4 space-y-4">
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-2 tracking-wide">Categories</div>
                <div className="space-y-1">
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={()=>{ setActiveCat(cat.id); setSelected(cat.presets[0] || null); }}
                      className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${activeCat===cat.id? 'border-indigo-300 bg-white text-indigo-700 shadow-sm' : 'border-gray-200 hover:bg-white text-gray-700'}`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold text-gray-600 mb-2 tracking-wide">Presets</div>
                <div className="space-y-1">
                  {active.presets.map(p => (
                    <button
                      key={p.id}
                      onClick={()=>{ setSelected(p); setCustomW(''); setCustomH(''); }}
                      className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${selected?.id===p.id? 'border-purple-300 bg-white text-purple-700 shadow-sm' : 'border-gray-200 hover:bg-white text-gray-700'}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-2 border-t border-gray-200">
                <div className="text-xs font-semibold text-gray-600 mb-2 tracking-wide">Custom</div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    className="w-24 h-9 px-3 rounded-md border border-gray-300"
                    placeholder="W"
                    value={customW}
                    onChange={(e)=>{ setSelected(null); setCustomW(e.target.value); }}
                  />
                  <span className="text-gray-500">×</span>
                  <input
                    type="number"
                    className="w-24 h-9 px-3 rounded-md border border-gray-300"
                    placeholder="H"
                    value={customH}
                    onChange={(e)=>{ setSelected(null); setCustomH(e.target.value); }}
                  />
                </div>
              </div>
            </div>
          </aside>

          {/* Right: preview */}
          <section className="p-6 overflow-y-auto h-full min-h-0">
            <div className="max-w-3xl mx-auto">
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  <span className="font-medium text-gray-900">Selected:</span>{' '}
                  {width} × {height} px
                </div>
                <div className="inline-flex items-center gap-2 text-xs text-gray-500">
                  <span>Aspect ratio</span>
                  <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-gray-700">
                    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="7" width="18" height="10" rx="2"/></svg>
                    {(width/height).toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="h-[420px] grid place-items-center bg-gray-50 rounded-lg">
                  <div
                    className="relative rounded-md bg-gradient-to-br from-indigo-500 to-purple-500 shadow-lg"
                    style={{ width: preview.w, height: preview.h }}
                  >
                    <div className="absolute inset-0 rounded-md ring-4 ring-white/60" />
                    <div className="absolute -top-3 -right-3 inline-flex items-center gap-1 rounded-full bg-white px-2 py-1 text-xs text-gray-700 shadow ring-1 ring-gray-200">
                      <svg className="h-3.5 w-3.5 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
                      {width}×{height}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-2">
                <button className="h-9 px-4 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-700" onClick={onClose}>Close</button>
                <button
                  className="h-9 px-4 rounded-md bg-gray-900 text-white hover:bg-gray-800"
                  onClick={() => { onApply?.({ width, height }); }}
                >
                  Apply
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DimensionPickerModal;
